import { FORM_INSTANCE_STATUS, QUESTION_TYPE } from "constants/incorporation";
import {
  FormTree,
  Question,
  Stage,
  Step,
} from "store/apis/incorporationFormBuilder";

export const isQuestionAnswered = (question: Question) => {
  if (question.question_type === QUESTION_TYPE.READ_ONLY) {
    return question.status === FORM_INSTANCE_STATUS.COMPLETED;
  }

  if (!question.answer) {
    return false;
  }

  if ("option_instance_id" in question.answer) {
    return Boolean(question.answer.option_instance_id);
  }

  if (question.question_type === QUESTION_TYPE.MULTI_DOC_UPLOAD) {
    return (
      "documents" in question.answer && question.answer.documents.length > 0
    );
  }

  if ("text" in question.answer) {
    return Boolean(question.answer.text);
  }

  return false;
};

export const getStepProgress = (step: Step) => {
  const totalCount = step.questions.length;
  const answeredCount = step.questions.filter(isQuestionAnswered).length;

  return { answeredCount, totalCount };
};

export const getStageProgress = (stage: Stage) => {
  const questions = stage.steps.flatMap((step) => step.questions);
  const totalCount = questions.length;
  const answeredCount = questions.filter(isQuestionAnswered).length;

  return { answeredCount, totalCount };
};

export const isStepAnswered = (step: Step) =>
  step.questions.length > 0 &&
  step.questions.every(
    (question) => !question.is_mandatory || isQuestionAnswered(question)
  );

export const isStageComplete = (stage: Stage) =>
  stage.steps.length > 0 && stage.steps.every(isStepAnswered);

const isOptionComplete = (option: { label: string }) => Boolean(option.label);

const isQuestionStructureComplete = (question: Question) => {
  if (!question.label) {
    return false;
  }

  if (question.question_type === QUESTION_TYPE.SINGLE_SELECT) {
    return question.options.every(isOptionComplete);
  }

  return true;
};

const isStepStructureComplete = (step: Step) =>
  Boolean(step.title) &&
  step.questions.length > 0 &&
  step.questions.every(isQuestionStructureComplete);

const isStageStructureComplete = (stage: Stage) =>
  Boolean(stage.title) &&
  stage.steps.length > 0 &&
  stage.steps.every(isStepStructureComplete);

export const isFormTreeComplete = (formTree: FormTree | undefined) => {
  if (!formTree) {
    return false;
  }

  return (
    formTree.stages.length > 0 &&
    formTree.stages.every(isStageStructureComplete)
  );
};
